/**
 * Several orientations, edited the way PVsyst edits them: ONE form, and a
 * choice of which plane it is showing.
 *
 * Each plane is a collapsible panel; at most one is open - any may be closed,
 * all of them together too - and the open one holds the Panel Orientation form - tilt, azimuth, what the tilt is measured
 * from, the optimiser and its two profile plots, the geometry sketch - which
 * ConfigForm passes in as children. Every plane therefore has exactly the same
 * controls; the only thing the primary does differently is carry the physical
 * layout, the 3D scene and auto-design, and its panel says so.
 *
 * A single-plane project shows the form as it always did. The button that
 * adds a plane sits above the form, or above the panels. OrientationStatus sits at the foot of the section:
 * what stops the plant from being simulated. Everything writes through
 * config/orientations.js, so a single-plane project is never touched until a
 * second plane is added.
 *
 * Sentences carry names and numbers, so no catalogue key can hold them; the
 * components word both languages themselves.
 */
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useIsGreek } from "../../context/LanguageContext";
import {
  addOrientation,
  autoName,
  bearing,
  displayName,
  formatAngle,
  orientationProblems,
  orientationsFromConfig,
  orientationSummary,
  problemMessage,
  removeOrientation,
  updateOrientation,
} from "../config/orientations";

const labelSx = {
  color: "primary.main",
  fontWeight: 600,
  letterSpacing: "0.02em",
  "&.Mui-focused": { color: "primary.main" },
};

// Keep keystrokes local to the input. Committing every character rebuilt the
// full project history and all orientation panels, making fast typing lag and
// occasionally overwrite the latest key. Commit once the user leaves the field.
function OrientationNameField({ value, onCommit, ...props }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <TextField
      {...props}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => { if (draft !== value) onCommit(draft); }}
    />
  );
}

function formatNumber(value, isGreek, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/** "3 MPPTs on 1 inverter · 150 modules (50%) · 88.5 kWp", in either language. */
export function attachedText(summary, plantKwp, isGreek) {
  if (!summary || summary.mppts === 0) {
    return isGreek ? "Δεν έχει συνδεθεί ακόμη MPPT." : "No MPPT attached yet.";
  }
  const share = summary.share != null ? ` (${formatNumber(summary.share * 100, isGreek)}%)` : "";
  const kwp = Number(plantKwp);
  const kwpText = Number.isFinite(kwp) && kwp > 0 && summary.share != null
    ? ` · ${formatNumber(kwp * summary.share, isGreek, 1)} kWp`
    : "";
  if (isGreek) {
    const inverters = summary.inverters === 1 ? "1 αντιστροφέα" : `${summary.inverters} αντιστροφείς`;
    const modules = summary.modules === 1 ? "1 πλαίσιο" : `${formatNumber(summary.modules, true)} πλαίσια`;
    return `${summary.mppts} MPPT σε ${inverters} · ${modules}${share}${kwpText}`;
  }
  const mppts = summary.mppts === 1 ? "1 MPPT" : `${summary.mppts} MPPTs`;
  const inverters = summary.inverters === 1 ? "1 inverter" : `${summary.inverters} inverters`;
  const modules = summary.modules === 1 ? "1 module" : `${formatNumber(summary.modules, false)} modules`;
  return `${mppts} on ${inverters} · ${modules}${share}${kwpText}`;
}

/**
 * The plant's planes as collapsible panels, at most one open.
 *
 * `children` is the orientation form; it is shown inside the panel of the
 * plane `activeId` names, and `onSelect` changes which one that is - `null`
 * when every panel is closed. Opening a panel closes whichever was open. On a
 * single-plane project there are no panels: the form is simply shown.
 */
export function OrientationPanels({
  cfg,
  activeId,
  onSelect,
  onChange,
  plantKwp = null,
  onOpenElectricalDesign = null,
  heading = null,
  children = null,
}) {
  const isGreek = useIsGreek();
  const tr = (el, en) => (isGreek ? el : en);
  const [notice, setNotice] = useState(null);

  const orientations = orientationsFromConfig(cfg);
  const multi = orientations.length > 1;
  const fixed = String(cfg?.mountType || "fixed") === "fixed";
  // No match (null, or a plane that was just removed) means every panel is closed.
  const active = orientations.find((o) => o.id === activeId) || null;
  const summaries = orientationSummary(cfg);
  const roofFlush = cfg?.panelOrientation?.installationType === "roof_flush";
  const hasDesign = summaries.some((s) => s.mppts > 0);

  const add = () => {
    const next = addOrientation(cfg);
    setNotice(null);
    onChange(next);
    onSelect(next.orientations[next.orientations.length - 1].id);
  };

  // The plane's MPPTs go back to orientation 1, and the screen says how many:
  // a design whose strings silently changed plane is the thing to avoid.
  const remove = (o) => {
    const { cfg: next, moved } = removeOrientation(cfg, o.id);
    const name = displayName(o, isGreek);
    onChange(next);
    onSelect(null);
    if (!moved) {
      setNotice(null);
      return;
    }
    setNotice(
      isGreek
        ? moved === 1
          ? `Το MPPT του «${name}» επέστρεψε στον προσανατολισμό 1.`
          : `Τα ${moved} MPPT του «${name}» επέστρεψαν στον προσανατολισμό 1.`
        : moved === 1
          ? `The MPPT on “${name}” is back on orientation 1.`
          : `The ${moved} MPPTs on “${name}” are back on orientation 1.`
    );
  };

  // The section title, when the caller passes one, shares the row with the
  // button instead of sitting on a line of its own above it.
  const addButton = (
    <Box sx={{ display: "flex", justifyContent: heading ? "space-between" : "flex-end",
               alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
      {heading}
      <Tooltip
        title={fixed
          ? tr(
              "Ένας σταθμός μπορεί να εκτείνεται σε περισσότερα επίπεδα — δύο όψεις στέγης, πεδίο ανατολής–δύσης, συστοιχίες με διαφορετική κλίση. Κάθε επίπεδο είναι ένας προσανατολισμός και κάθε MPPT συνδέεται σε έναν από αυτούς στον ηλεκτρολογικό σχεδιασμό.",
              "A plant can stand on several planes — two faces of a roof, an east–west field, arrays at different tilts. Each plane is an orientation, and each MPPT is attached to one of them in the Electrical design."
            )
          : tr(
              "Οι πολλαπλοί προσανατολισμοί είναι διαθέσιμοι μόνο για σταθμούς σταθερής κλίσης.",
              "Several orientations are available for fixed-tilt plants only."
            )}
        arrow
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            disabled={!fixed}
            onClick={add}
            sx={{
              minWidth: 0,
              minHeight: 26,
              px: 1,
              py: 0.25,
              fontSize: "0.7rem",
              lineHeight: 1.2,
              fontWeight: 600,
              textTransform: "none",
              "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
              "& .MuiButton-startIcon svg": { fontSize: "0.9rem" },
            }}
          >
            {roofFlush
              ? tr("Προσθήκη πλευράς στέγης", "Add roof face")
              : tr("Προσθήκη προσανατολισμού", "Add orientation")}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  const noticeAlert = notice ? (
    <Alert severity="info" sx={{ mb: 1.25 }} onClose={() => setNotice(null)}>
      {notice}
    </Alert>
  ) : null;

  if (!multi) {
    // The form as it always was, and one way to add a plane. Removing the last
    // further plane lands here too, so the notice is shown on this view.
    return (
      <Box data-testid="orientation-panels">
        {addButton}
        {noticeAlert}
        {children}
      </Box>
    );
  }

  return (
    <Box data-testid="orientation-panels">
      {addButton}
      {noticeAlert}
      {orientations.map((o, i) => {
        const open = Boolean(active) && o.id === active.id;
        const summary = summaries[i];
        const unattached = hasDesign && summary?.mppts === 0;
        // A custom name hides the angles, so the header states them.
        const angles = !o.autoName && o.name
          ? tr(
              `κλίση ${formatAngle(o.tilt, true)}°, αζ. ${formatAngle(o.azim, true)}°${o.azim != null ? ` (${bearing(o.azim, true)})` : ""}`,
              `tilt ${formatAngle(o.tilt)}°, az. ${formatAngle(o.azim)}°${o.azim != null ? ` (${bearing(o.azim)})` : ""}`
            )
          : null;
        return (
          <Accordion
            key={o.id}
            expanded={open}
            onChange={(e, expanded) => {
              setNotice(null);
              onSelect(expanded ? o.id : null);
            }}
            disableGutters
            variant="outlined"
            TransitionProps={{ unmountOnExit: true }}
            data-testid={`orientation-panel-${o.id}`}
            sx={{ mb: 1, borderRadius: 1, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`${i + 1} · ${roofFlush ? `${tr("Πλευρά στέγης", "Roof face")} · ` : ""}${displayName(o, isGreek)}`}
                </Typography>
                {i === 0 ? <Chip size="small" label={tr("Κύριος", "Primary")} /> : null}
                {angles ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{angles}</Typography>
                ) : null}
                {hasDesign ? (
                  <Typography variant="body2" sx={{ color: unattached ? "warning.main" : "text.secondary" }}>
                    {`· ${attachedText(summary, plantKwp, isGreek)}`}
                  </Typography>
                ) : null}
                {unattached ? <WarningAmberIcon fontSize="small" color="warning" /> : null}
              </Box>
            </AccordionSummary>
            {open ? (
              <AccordionDetails sx={{ pt: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.75 }}>
                  <OrientationNameField
                    size="small"
                    label={tr("Όνομα", "Name")}
                    value={o.autoName ? "" : o.name}
                    placeholder={autoName(o.tilt, o.azim, isGreek)}
                    onCommit={(name) => {
                      setNotice(null);
                      onChange(updateOrientation(cfg, o.id, { name }));
                    }}
                    sx={{ width: { xs: "100%", sm: 280 } }}
                    InputLabelProps={{ shrink: true, sx: labelSx }}
                  />
                  <Box sx={{ flex: 1 }} />
                  {onOpenElectricalDesign ? (
                    <Button size="small" variant="text" onClick={onOpenElectricalDesign}>
                      {tr("Σύνδεση MPPT στον ηλεκτρολογικό σχεδιασμό", "Attach MPPTs in the Electrical design")}
                    </Button>
                  ) : null}
                  {i > 0 ? (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon fontSize="small" />}
                      onClick={() => remove(o)}
                      aria-label={tr(`Αφαίρεση προσανατολισμού ${i + 1}`, `Remove orientation ${i + 1}`)}
                    >
                      {tr("Αφαίρεση", "Remove")}
                    </Button>
                  ) : null}
                </Box>
                <Typography variant="caption" sx={{ display: "block", mb: 1.25, color: "text.secondary" }}>
                  {roofFlush
                    ? tr(
                        "Κάθε πλευρά έχει δική της κλίση και φορά στέγης. Μπορείτε να τις ορίσετε πριν συνδέσετε τα MPPT και πριν σχεδιάσετε τη φυσική διάταξη της κάθε πλευράς.",
                        "Each roof face has its own pitch and facing. Set them before attaching MPPTs and laying out the modules on each face."
                      )
                    : i === 0
                    ? tr(
                        "Κύριος προσανατολισμός: ο αυτόματος σχεδιασμός προτείνει τη διάταξή του. Κάθε προσανατολισμός αποθηκεύει τη δική του φυσική διάταξη και μέτρηση σκίασης από εμπόδια.",
                        "Primary orientation: auto-design proposes its layout. Each orientation stores its own physical layout and obstacle-shading measurement."
                      )
                    : tr(
                        "Κάθε επίπεδο προσομοιώνεται χωριστά με τη δική του διάταξη και μέτρηση εμποδίων. Αν δεν έχει δική του διάταξη, χρησιμοποιούνται οι αποστάσεις του κύριου προσανατολισμού· η μέτρηση εμποδίων του κύριου δεν αντιγράφεται.",
                        "Each plane is simulated with its own layout and obstacle measurement. Without its own layout, it uses the primary's row spacing; the primary's obstacle measurement is not copied."
                      )}
                </Typography>
                {children}
              </AccordionDetails>
            ) : null}
          </Accordion>
        );
      })}
    </Box>
  );
}

/** What stops the plant from being simulated; nothing at all when it is ready. */
export function OrientationStatus({ cfg }) {
  const isGreek = useIsGreek();
  const tr = (el, en) => (isGreek ? el : en);
  const problems = orientationProblems(cfg);
  if (!problems.length) return null;
  return (
    <Alert severity="warning" sx={{ mt: 1.25 }} data-testid="orientation-problems">
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {tr("Δεν είναι έτοιμο για προσομοίωση", "Not ready to simulate")}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {problems.map((p, i) => (
          <li key={`${p.code}-${p.id || p.tag || i}`}>
            <Typography variant="body2">{problemMessage(p, cfg, isGreek)}</Typography>
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {tr(
          "Το έργο αποθηκεύεται κανονικά· η προσομοίωση εκτελείται μόλις επιλυθούν αυτά.",
          "The project saves as usual; the simulation runs once these are resolved."
        )}
      </Typography>
    </Alert>
  );
}
